import { computed, onBeforeUnmount, ref, watch } from 'vue'

const DRAG_THRESHOLD = 4

export function useDraggablePosition(getConfig) {
  const offsetX = ref(0)
  const offsetY = ref(0)
  const isDragging = ref(false)

  let activePointerId = null
  let startClientX = 0
  let startClientY = 0
  let startOffsetX = 0
  let startOffsetY = 0
  let suppressNextClick = false
  let clickResetTimer = null

  const isDraggable = computed(() => !!getConfig()?.draggable)

  const draggableStyle = computed(() => {
    if (!isDraggable.value) {
      return {}
    }

    const style = {
      cursor: isDragging.value ? 'grabbing' : 'grab',
      touchAction: 'none',
      translate: `${offsetX.value}px ${offsetY.value}px`,
      willChange: 'translate',
    }

    if (isDragging.value) {
      style.userSelect = 'none'
    }

    return style
  })

  function removeWindowListeners() {
    if (typeof window === 'undefined')
      return
    window.removeEventListener('pointermove', onPointerMove)
    window.removeEventListener('pointerup', finishDrag)
    window.removeEventListener('pointercancel', finishDrag)
  }

  function resetPointerState() {
    activePointerId = null
    isDragging.value = false
    removeWindowListeners()
  }

  function resetPosition() {
    offsetX.value = 0
    offsetY.value = 0
    suppressNextClick = false
    if (typeof window !== 'undefined') {
      window.clearTimeout(clickResetTimer)
    }
    resetPointerState()
  }

  function onPointerDown(event) {
    if (
      !isDraggable.value
      || event.isPrimary === false
      || (event.pointerType === 'mouse' && event.button !== 0)
    ) {
      return
    }

    event.stopPropagation()
    activePointerId = event.pointerId
    startClientX = event.clientX
    startClientY = event.clientY
    startOffsetX = offsetX.value
    startOffsetY = offsetY.value

    if (typeof window !== 'undefined') {
      window.addEventListener('pointermove', onPointerMove, { passive: false })
      window.addEventListener('pointerup', finishDrag)
      window.addEventListener('pointercancel', finishDrag)
    }
  }

  function onPointerMove(event) {
    if (event.pointerId !== activePointerId)
      return

    const deltaX = event.clientX - startClientX
    const deltaY = event.clientY - startClientY

    if (
      !isDragging.value
      && Math.hypot(deltaX, deltaY) < DRAG_THRESHOLD
    ) {
      return
    }

    if (!isDragging.value) {
      isDragging.value = true
      suppressNextClick = true
    }

    event.preventDefault()
    offsetX.value = startOffsetX + deltaX
    offsetY.value = startOffsetY + deltaY
  }

  function finishDrag(event) {
    if (event.pointerId !== activePointerId)
      return
    resetPointerState()

    if (suppressNextClick && typeof window !== 'undefined') {
      window.clearTimeout(clickResetTimer)
      clickResetTimer = window.setTimeout(() => {
        suppressNextClick = false
      }, 0)
    }
  }

  function onClickCapture(event) {
    if (!suppressNextClick)
      return
    suppressNextClick = false
    if (typeof window !== 'undefined') {
      window.clearTimeout(clickResetTimer)
    }
    event.preventDefault()
    event.stopPropagation()
  }

  function onNativeDragStart(event) {
    if (isDraggable.value) {
      event.preventDefault()
    }
  }

  watch(isDraggable, (enabled) => {
    if (enabled)
      return
    resetPosition()
  })

  watch(() => getConfig(), (config, previousConfig) => {
    if (config !== previousConfig) {
      resetPosition()
    }
  })

  onBeforeUnmount(() => {
    if (typeof window !== 'undefined') {
      window.clearTimeout(clickResetTimer)
    }
    resetPosition()
  })

  return {
    isDraggable,
    isDragging,
    draggableStyle,
    onPointerDown,
    onClickCapture,
    onNativeDragStart,
  }
}
